<script lang="ts">
  import { ArrowLeft, ArrowRight, Check, Plus, Search, X, Sparkles } from "lucide-svelte";
  import {
    BOOKING_OPTIONS,
    emptyProfileDraft,
    FLIGHT_OPTIONS,
    HOTEL_OPTIONS,
    PAY_OPTIONS,
    SCENARIO_OPTIONS,
    recordCardRequest,
    submitCardRequest,
    type Profile,
    type CustomCard
  } from "./profile";
  import type { WalletCard } from "../wallet/types";
  import Button from "./ui/Button.svelte";
  import Logo from "./Logo.svelte";
  import { cn } from "./lib/utils";
  import { toast } from "./toast";

  export let cards: WalletCard[] = [];
  export let customCards: CustomCard[] = [];
  export let initialSelected: string[] = [];
  export let onComplete: (profile: Profile, selectedIds: string[], customCards: CustomCard[]) => void;

  let step = 1;
  const totalSteps = 6;
  let draft = emptyProfileDraft();
  let selectedIds: Set<string> = new Set(initialSelected);
  let cardSearch = "";
  let showCustomForm = false;
  let custom = {
    name: "",
    issuer: "",
    level: "白金卡",
    annualFeeText: "",
    email: "",
    note: ""
  };
  let workingCustomCards: CustomCard[] = [...customCards];

  function toggleScenario(id: typeof SCENARIO_OPTIONS[number]["id"]) {
    if (draft.scenarios.includes(id)) {
      draft.scenarios = draft.scenarios.filter(x => x !== id);
    } else if (draft.scenarios.length < 3) {
      draft.scenarios = [...draft.scenarios, id];
    } else {
      toast.info("最多选 3 项", { description: "聚焦最常用的几个支付场景。" });
    }
  }

  function toggleCard(id: string) {
    if (selectedIds.has(id)) {
      selectedIds.delete(id);
    } else {
      selectedIds.add(id);
    }
    selectedIds = new Set(selectedIds);
  }

  function next() {
    if (step < totalSteps) {
      step += 1;
    } else {
      const profile: Profile = { ...draft, completedAt: new Date().toISOString() };
      onComplete(profile, [...selectedIds], workingCustomCards);
    }
  }

  function back() {
    if (step > 1) step -= 1;
  }

  function startCustomForm() {
    showCustomForm = true;
    custom = {
      name: cardSearch,
      issuer: "",
      level: "白金卡",
      annualFeeText: "",
      email: "",
      note: ""
    };
  }
  function cancelCustomForm() {
    showCustomForm = false;
  }

  async function saveCustomCard() {
    if (!custom.name.trim() || !custom.issuer.trim()) return;
    const id = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const newCard: CustomCard = {
      id,
      name: custom.name.trim(),
      issuer: custom.issuer.trim(),
      level: custom.level.trim() || "信用卡",
      annualFeeText: custom.annualFeeText.trim() || "年费用户自录",
      reportedAt: new Date().toISOString()
    };
    workingCustomCards = [...workingCustomCards, newCard];
    selectedIds.add(id);
    selectedIds = new Set(selectedIds);

    const req = recordCardRequest({
      cardName: newCard.name,
      issuer: newCard.issuer,
      level: newCard.level,
      annualFeeText: newCard.annualFeeText,
      email: custom.email.trim(),
      note: custom.note.trim()
    });
    submitCardRequest(req);
    toast.success(`已加入卡包：${newCard.name}`, {
      description: "我们会按收录请求频次安排正式录入。"
    });

    showCustomForm = false;
    cardSearch = "";
  }

  function removeCustomCard(id: string) {
    workingCustomCards = workingCustomCards.filter(c => c.id !== id);
    selectedIds.delete(id);
    selectedIds = new Set(selectedIds);
  }

  $: canAdvance =
    step === 1 ? draft.scenarios.length > 0 :
    step === 6 ? selectedIds.size > 0 :
    true;

  $: progressPct = (step / totalSteps) * 100;

  $: filteredCards = (() => {
    const q = cardSearch.trim().toLowerCase();
    if (!q) return cards.slice(0, 24);
    return cards
      .filter(c => {
        const hay = [c.name, c.shortName, c.issuer, ...(c.aliases || [])].join(" ").toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 24);
  })();

  $: canSaveCustom = custom.name.trim().length > 0 && custom.issuer.trim().length > 0;

  type StepMeta = { title: string; sub: string; tone: string; emoji: string };
  const STEP_META: StepMeta[] = [
    { title: "你的卡，平时最常付什么？", sub: "最多选 3 项 · 这影响「随手刷」推哪张卡", tone: "32 92% 50%", emoji: "🛒" },
    { title: "今年你坐了几次飞机？", sub: "决定要不要推贵宾厅 / 接送机", tone: "210 80% 60%", emoji: "✈️" },
    { title: "今年你住了几次酒店？", sub: "决定酒店权益是否进首页", tone: "30 65% 55%", emoji: "🏨" },
    { title: "预约型权益你会用吗？", sub: "洗牙 / 体检 / SPA / 快剪 这类", tone: "158 55% 50%", emoji: "🗓️" },
    { title: "手机里最常用的付款方式？", sub: "默认卡推荐会绑定你这个付款入口", tone: "36 96% 60%", emoji: "📱" },
    { title: "添加你持有的卡", sub: "找不到？下方「自录」可以自己加 · 我们会提交收录请求", tone: "270 60% 70%", emoji: "🪪" }
  ];

  $: meta = STEP_META[step - 1];
</script>

<div class="relative mx-auto flex min-h-screen max-w-2xl flex-col px-5 pt-8 pb-6">
  <!-- ambient backdrop per step -->
  <div
    aria-hidden="true"
    class="pointer-events-none absolute inset-0 -z-10"
    style="background:
      radial-gradient(ellipse 80% 40% at 50% 0%, hsl({meta.tone} / 0.18), transparent 60%);
      transition: background 0.45s ease;"
  ></div>

  <header class="mb-8 space-y-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <Logo size={22} />
        <span class="font-display text-sm">魔法小卡</span>
      </div>
      <span class="font-label text-muted-foreground">
        STEP {step} / {totalSteps}
      </span>
    </div>

    <!-- segmented progress -->
    <div class="flex gap-1.5">
      {#each Array(totalSteps) as _, i}
        <div
          class={cn(
            "h-1.5 flex-1 rounded-full transition-all duration-500",
            i + 1 < step && "bg-primary",
            i + 1 === step && "bg-primary anim-shimmer",
            i + 1 > step && "bg-foreground/10"
          )}
        ></div>
      {/each}
    </div>
  </header>

  <div class="flex-1">
    {#key step}
      <div class="anim-fade-in-up space-y-2">
        <div class="flex items-center gap-2">
          <span
            class="flex size-9 items-center justify-center rounded-full text-lg"
            style="background: hsl({meta.tone} / 0.20);"
          >
            {meta.emoji}
          </span>
          <span class="font-label text-muted-foreground">第 {step} 题</span>
        </div>
        <h1 class="font-display text-[1.7rem] leading-tight">{meta.title}</h1>
        <p class="text-sm text-muted-foreground">{meta.sub}</p>

        {#if step === 1}
          <div class="grid grid-cols-1 gap-2.5 pt-5 sm:grid-cols-2">
            {#each SCENARIO_OPTIONS as opt, i}
              {@const active = draft.scenarios.includes(opt.id)}
              <button
                type="button"
                class={cn(
                  "anim-fade-in-up focus-amber flex items-center gap-3 rounded-2xl border bg-card px-4 py-3.5 text-left text-sm transition-all hover:-translate-y-0.5 hover:border-primary/60",
                  active && "border-primary bg-primary/8 font-medium text-foreground ring-1 ring-primary/30"
                )}
                style="animation-delay: {i * 40}ms;"
                on:click={() => toggleScenario(opt.id)}
              >
                <span class="text-xl leading-none">{opt.emoji}</span>
                <span class="flex-1">{opt.label}</span>
                {#if active}
                  <span class="flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground anim-pop">
                    <Check class="size-3" />
                  </span>
                {/if}
              </button>
            {/each}
          </div>
          <p class="pt-3 text-xs text-muted-foreground">已选 {draft.scenarios.length} / 3</p>
        {/if}

        {#if step === 2}
          <div class="grid grid-cols-2 gap-2.5 pt-5">
            {#each FLIGHT_OPTIONS as opt, i}
              {@const active = draft.flights === opt.id}
              <button
                type="button"
                class={cn(
                  "anim-fade-in-up focus-amber rounded-2xl border bg-card px-4 py-5 text-base transition-all hover:-translate-y-0.5 hover:border-primary/60",
                  active && "border-primary bg-primary/8 font-semibold text-foreground ring-1 ring-primary/30"
                )}
                style="animation-delay: {i * 40}ms;"
                on:click={() => (draft.flights = opt.id)}
              >
                {opt.label}
              </button>
            {/each}
          </div>
        {/if}

        {#if step === 3}
          <div class="grid grid-cols-3 gap-2.5 pt-5">
            {#each HOTEL_OPTIONS as opt, i}
              {@const active = draft.hotels === opt.id}
              <button
                type="button"
                class={cn(
                  "anim-fade-in-up focus-amber rounded-2xl border bg-card px-4 py-5 text-base transition-all hover:-translate-y-0.5 hover:border-primary/60",
                  active && "border-primary bg-primary/8 font-semibold text-foreground ring-1 ring-primary/30"
                )}
                style="animation-delay: {i * 40}ms;"
                on:click={() => (draft.hotels = opt.id)}
              >
                {opt.label}
              </button>
            {/each}
          </div>
        {/if}

        {#if step === 4}
          <div class="grid grid-cols-3 gap-2.5 pt-5">
            {#each BOOKING_OPTIONS as opt, i}
              {@const active = draft.bookingWill === opt.id}
              <button
                type="button"
                class={cn(
                  "anim-fade-in-up focus-amber rounded-2xl border bg-card px-4 py-5 text-base transition-all hover:-translate-y-0.5 hover:border-primary/60",
                  active && "border-primary bg-primary/8 font-semibold text-foreground ring-1 ring-primary/30"
                )}
                style="animation-delay: {i * 40}ms;"
                on:click={() => (draft.bookingWill = opt.id)}
              >
                {opt.label}
              </button>
            {/each}
          </div>
        {/if}

        {#if step === 5}
          <div class="grid grid-cols-2 gap-2.5 pt-5">
            {#each PAY_OPTIONS as opt, i}
              {@const active = draft.pay === opt.id}
              <button
                type="button"
                class={cn(
                  "anim-fade-in-up focus-amber rounded-2xl border bg-card px-4 py-5 text-base transition-all hover:-translate-y-0.5 hover:border-primary/60",
                  active && "border-primary bg-primary/8 font-semibold text-foreground ring-1 ring-primary/30"
                )}
                style="animation-delay: {i * 40}ms;"
                on:click={() => (draft.pay = opt.id)}
              >
                {opt.label}
              </button>
            {/each}
          </div>
        {/if}

        {#if step === 6}
          <div class="relative pt-5">
            <Search class="absolute left-3 top-[calc(50%+10px)] size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="搜卡名、发卡行、别名…"
              bind:value={cardSearch}
              class="focus-amber w-full rounded-2xl border bg-card px-10 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
            {#if cardSearch}
              <button
                type="button"
                class="absolute right-3 top-[calc(50%+10px)] -translate-y-1/2 text-muted-foreground hover:text-foreground"
                on:click={() => (cardSearch = "")}
              >
                <X class="size-4" />
              </button>
            {/if}
          </div>

          {#if workingCustomCards.length > 0}
            <div class="pt-3">
              <p class="mb-2 text-xs text-muted-foreground">你自录的卡：</p>
              <div class="space-y-2">
                {#each workingCustomCards as cc}
                  <div class="anim-fade-in-up flex items-center justify-between rounded-xl border border-dashed bg-card/60 px-3 py-2.5 text-sm">
                    <div class="flex flex-col">
                      <span class="font-medium">{cc.name}</span>
                      <span class="text-[11px] text-muted-foreground">{cc.issuer} · {cc.level} · {cc.annualFeeText}</span>
                    </div>
                    <button
                      type="button"
                      class="text-xs text-muted-foreground hover:text-destructive"
                      on:click={() => removeCustomCard(cc.id)}
                    >
                      移除
                    </button>
                  </div>
                {/each}
              </div>
            </div>
          {/if}

          {#if !showCustomForm}
            <div class="grid grid-cols-1 gap-2.5 pt-4 sm:grid-cols-2">
              {#each filteredCards as card, i}
                {@const active = selectedIds.has(card.id)}
                <button
                  type="button"
                  class={cn(
                    "anim-fade-in-up focus-amber relative flex flex-col gap-1 rounded-2xl border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/60",
                    active && "border-primary bg-primary/8 ring-1 ring-primary/30"
                  )}
                  style="animation-delay: {Math.min(i * 30, 600)}ms;"
                  on:click={() => toggleCard(card.id)}
                >
                  <span class="text-sm font-semibold">{card.shortName || card.name}</span>
                  <span class="text-xs text-muted-foreground">{card.issuer} · {card.level}</span>
                  <span class="text-xs text-muted-foreground/80">{card.annualFeeText}</span>
                  {#if active}
                    <span class="absolute right-3 top-3 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground anim-pop">
                      <Check class="size-3" />
                    </span>
                  {/if}
                </button>
              {/each}
            </div>

            {#if filteredCards.length === 0}
              <div class="anim-fade-in-up mt-4 rounded-2xl border border-dashed bg-card/40 px-5 py-6 text-center">
                <p class="text-sm font-medium">「{cardSearch}」没找到</p>
                <p class="mt-1 text-xs text-muted-foreground">先自录一下吧，我们会把它加进收录队列。</p>
              </div>
            {/if}

            <div class="pt-4">
              <Button variant="outline" on:click={startCustomForm} class="w-full">
                <Plus class="mr-1.5 size-4" />
                找不到我的卡，自己录一张
              </Button>
            </div>

            <p class="pt-3 text-xs text-muted-foreground">已选 {selectedIds.size} 张</p>
          {:else}
            <div class="anim-fade-in-up mt-4 rounded-2xl border-2 border-primary/40 bg-card p-5 space-y-3">
              <div class="flex items-center gap-2">
                <Sparkles class="size-4 text-primary" />
                <p class="text-sm font-semibold">自录一张卡</p>
              </div>
              <p class="text-xs text-muted-foreground">
                会按你填的资料加进卡包；同时把「我有这张卡」这个信号提交到收录队列。
              </p>
              <div class="space-y-2 pt-2">
                <label class="block">
                  <span class="text-xs text-muted-foreground">卡名称（必填）</span>
                  <input
                    type="text"
                    bind:value={custom.name}
                    placeholder="如：中信i白金"
                    class="focus-amber mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  />
                </label>
                <label class="block">
                  <span class="text-xs text-muted-foreground">发卡行（必填）</span>
                  <input
                    type="text"
                    bind:value={custom.issuer}
                    placeholder="如：中信银行 / HSBC HK"
                    class="focus-amber mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  />
                </label>
                <div class="grid grid-cols-2 gap-2">
                  <label class="block">
                    <span class="text-xs text-muted-foreground">等级</span>
                    <input
                      type="text"
                      bind:value={custom.level}
                      placeholder="白金卡 / 钻石卡"
                      class="focus-amber mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </label>
                  <label class="block">
                    <span class="text-xs text-muted-foreground">年费</span>
                    <input
                      type="text"
                      bind:value={custom.annualFeeText}
                      placeholder="¥3600/年"
                      class="focus-amber mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                    />
                  </label>
                </div>
                <label class="block">
                  <span class="text-xs text-muted-foreground">邮箱（选填 · 只用于按收录请求频次排优先级，暂不主动发邮件）</span>
                  <input
                    type="email"
                    bind:value={custom.email}
                    placeholder="you@example.com"
                    class="focus-amber mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary"
                  />
                </label>
              </div>
              <div class="flex gap-2 pt-2">
                <Button variant="outline" on:click={cancelCustomForm} class="flex-1">取消</Button>
                <Button on:click={saveCustomCard} disabled={!canSaveCustom} class="flex-[2]">
                  保存并提交
                </Button>
              </div>
            </div>
          {/if}
        {/if}
      </div>
    {/key}
  </div>

  {#if !showCustomForm}
    <footer
      class="sticky bottom-0 mt-8 flex gap-3 border-t border-foreground/10 bg-background/95 pt-5 pb-2 backdrop-blur"
    >
      <Button variant="outline" size="lg" disabled={step === 1} on:click={back} class="flex-1">
        <ArrowLeft class="mr-2 size-4" />
        上一步
      </Button>
      <Button size="lg" disabled={!canAdvance} on:click={next} class="flex-[2]">
        {step === totalSteps ? "✦ 完成，看看我的卡包" : "下一步"}
        {#if step !== totalSteps}
          <ArrowRight class="ml-2 size-4" />
        {/if}
      </Button>
    </footer>
  {/if}
</div>
